import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast, Toaster } from 'sonner';
import {
  Target, TrendingUp, DollarSign, Clock, Shield, Star,
  RefreshCw, Filter, Bookmark, Play, X, AlertTriangle, BarChart3, Zap, ExternalLink, Hammer
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DraftBlock = ({ draft }) => {
  const [open, setOpen] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft.content || '');
      toast.success('Copied — ready to paste');
    } catch { toast.error('Copy failed'); }
  };
  return (
    <div className="mt-2 rounded-lg bg-[#F59E0B]/[0.05] border border-[#F59E0B]/20" data-testid="lee-draft">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button onClick={() => setOpen(!open)} className="text-[13px] font-semibold text-[#F59E0B] text-left flex-1 min-w-0 truncate" data-testid="draft-toggle">
          LEE wrote: {draft.title}
        </button>
        <button onClick={copy} className="text-[11px] px-2.5 py-1 rounded bg-[#F59E0B] text-black font-bold hover:bg-[#F59E0B]/90 shrink-0" data-testid="draft-copy-btn">
          Copy
        </button>
      </div>
      {open && (
        <pre className="px-3 pb-3 text-[12px] text-gray-300 whitespace-pre-wrap font-sans" data-testid="draft-content">{draft.content}</pre>
      )}
    </div>
  );
};

const InitiativeCard = ({ ini, onRefresh }) => {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const earned = ini.total_earned || 0;
  const est = ini.estimate_base || 0;
  const pct = est > 0 ? Math.min((earned / est) * 100, 100) : 0;

  const logIncome = async () => {
    const val = parseFloat(amount);
    if (!val) { toast.error('Enter a real dollar amount'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/initiatives/${ini.id}/income`, { amount: val, note }, { headers: getAuthHeaders() });
      toast.success(`$${Math.abs(val).toLocaleString()} ${val > 0 ? 'earned logged' : 'correction logged'} — proof over promises`);
      setAmount(''); setNote('');
      onRefresh();
    } catch (e) { toast.error('Failed to log income'); }
    setBusy(false);
  };

  const completeStep = async (idx) => {
    try {
      await axios.post(`${API}/initiatives/${ini.id}/milestones/${idx}/complete`, {}, { headers: getAuthHeaders() });
      onRefresh();
    } catch (e) { toast.error('Failed to complete step'); }
  };

  return (
    <div className="bg-[#141416] border border-gray-800 rounded-lg p-5" data-testid="initiative-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-white font-semibold">{ini.title}</h3>
          <div className="text-[12px] text-gray-500 mt-0.5">{ini.steps_done}/{ini.steps_total} steps done · started {new Date(ini.created_at).toLocaleDateString()}</div>
        </div>
        <div className="text-right">
          <div className={`text-xl font-bold font-mono ${earned > 0 ? 'text-emerald-400' : 'text-gray-500'}`} data-testid="initiative-earned">
            ${earned.toLocaleString()}
          </div>
          <div className="text-[11px] text-gray-500">proven{est ? ` vs $${est.toLocaleString()} est` : ''}</div>
        </div>
      </div>
      {est > 0 && (
        <div className="mt-2 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
          <div className="h-full bg-emerald-400/80 transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
      <div className="mt-3 space-y-1">
        {(ini.milestones || []).map((m, i) => (
          <div key={i} className="flex items-center gap-2 text-[13px]">
            {m.status === 'done' ? (
              <span className="text-emerald-400">✓</span>
            ) : (
              <button onClick={() => completeStep(i)} className="w-4 h-4 rounded border border-gray-600 hover:border-emerald-400 transition-colors shrink-0" data-testid="initiative-step-complete" title="Mark done" />
            )}
            <span className={m.status === 'done' ? 'text-gray-500 line-through' : 'text-gray-300'}>{m.step}</span>
          </div>
        ))}
      </div>
      {ini.lee_status === 'preparing' && (
        <div className="mt-3 flex items-center gap-2 text-[13px] text-[#F59E0B]" data-testid="lee-preparing">
          <RefreshCw className="w-3.5 h-3.5 animate-spin" /> LEE is writing your materials — refresh in a minute.
        </div>
      )}
      {(ini.your_steps || []).length > 0 && (
        <div className="mt-3 rounded-lg bg-blue-500/[0.06] border border-blue-500/20 p-3" data-testid="your-part">
          <div className="text-[11px] font-bold text-blue-300 uppercase tracking-wider mb-1.5">
            Your part{ini.effort_note ? ` — ${ini.effort_note}` : ''}
          </div>
          {ini.your_steps.map((s, i) => (
            <div key={i} className="text-[13px] text-gray-300 flex items-start gap-2">
              <span className="text-blue-400 font-bold">{i + 1}.</span>
              <span>{s.step}{s.minutes ? <span className="text-gray-500"> (~{s.minutes} min)</span> : null}</span>
            </div>
          ))}
        </div>
      )}
      {(ini.lee_drafts || []).map((d, i) => (
        <DraftBlock key={i} draft={d} />
      ))}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder="$ earned"
          className="w-28 bg-black/40 border border-gray-700 rounded px-2 py-1.5 text-sm text-white placeholder-gray-600" data-testid="income-amount-input" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="note (optional)"
          className="flex-1 min-w-[120px] bg-black/40 border border-gray-700 rounded px-2 py-1.5 text-sm text-white placeholder-gray-600" data-testid="income-note-input" />
        <button onClick={logIncome} disabled={busy}
          className="px-3 py-1.5 bg-emerald-500/90 text-black text-sm font-bold rounded hover:bg-emerald-400 transition-colors disabled:opacity-50" data-testid="income-log-btn">
          Log real income
        </button>
      </div>
    </div>
  );
};

const Opportunities = () => {
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [wealthState, setWealthState] = useState(null);
  const [stats, setStats] = useState(null);
  const [bestMove, setBestMove] = useState(null);
  const [initiatives, setInitiatives] = useState([]);
  const [filters, setFilters] = useState({
    category: '',
    capitalRange: 100000,
    riskMax: 10
  });
  const [simulateModal, setSimulateModal] = useState({ open: false, opportunity: null, data: null, loading: false });
  const [executingId, setExecutingId] = useState(null);
  const [dealRadar, setDealRadar] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllData();
  }, []);

  // While LEE is writing materials, refresh initiatives quietly every 5s
  useEffect(() => {
    const preparing = initiatives.some((i) => i.status === 'active' && i.lee_status === 'preparing');
    if (!preparing) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/initiatives`, { headers: getAuthHeaders() });
        setInitiatives(r.data.initiatives || []);
      } catch { /* quiet */ }
    }, 5000);
    return () => clearInterval(t);
  }, [initiatives]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [oppsRes, stateRes, statsRes, moveRes, iniRes, radarRes] = await Promise.allSettled([
        axios.get(`${API}/opportunities`, { headers: getAuthHeaders() }),
        axios.get(`${API}/wealth-state`, { headers: getAuthHeaders() }),
        axios.get(`${API}/opportunities/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/opportunities/best-move`, { headers: getAuthHeaders() }),
        axios.get(`${API}/initiatives`, { headers: getAuthHeaders() }),
        axios.get(`${API}/acquisition/top?limit=3`, { headers: getAuthHeaders() })
      ]);

      if (oppsRes.status === 'fulfilled') setOpportunities(oppsRes.value.data.opportunities || []);
      if (radarRes.status === 'fulfilled') setDealRadar(radarRes.value.data.opportunities || []);
      if (stateRes.status === 'fulfilled') setWealthState(stateRes.value.data.wealth_state || {});
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data || {});
      if (moveRes.status === 'fulfilled') setBestMove(moveRes.value.data || null);
      if (iniRes.status === 'fulfilled') setInitiatives(iniRes.value.data.initiatives || []);

      const failed = [oppsRes, stateRes, statsRes].filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        console.error('Failed to fetch some opportunity data:', failed);
        toast.error(oppsRes.status === 'rejected'
          ? 'Failed to load opportunities'
          : 'Some wealth data failed to load — showing what we have');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async () => {
    try {
      setScanning(true);
      const res = await axios.post(`${API}/opportunities/scan`, {}, { headers: getAuthHeaders() });
      toast.success(`Scan complete! Found ${res.data.opportunities_found} opportunities`);
      fetchAllData();
    } catch (error) {
      console.error('Scan failed:', error);
      toast.error('Failed to scan opportunities');
    } finally {
      setScanning(false);
    }
  };

  const handleSave = async (oppId) => {
    try {
      await axios.post(`${API}/opportunities/${oppId}/save`, {}, { headers: getAuthHeaders() });
      toast.success('Opportunity saved');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleIgnore = async (oppId) => {
    try {
      await axios.post(`${API}/opportunities/${oppId}/ignore`, {}, { headers: getAuthHeaders() });
      toast.success('Opportunity ignored');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to ignore');
    }
  };

  const handleStart = async (oppId) => {
    return handleExecute(oppId);
  };

  const handleExecute = async (oppId) => {
    setExecutingId(oppId);
    try {
      const res = await axios.post(`${API}/opportunities/${oppId}/execute`, {}, { headers: getAuthHeaders() });
      const { next, message, tasks_created } = res.data;
      if (next?.type === 'project') {
        toast.success(message || 'LEE started the full build');
        fetchAllData();
        setTimeout(() => navigate(`/inventions/${next.project_id}/builders`), 900);
      } else if (next?.type === 'url') {
        toast.success(`${message} (${tasks_created?.length || 0} tasks queued)`);
        window.open(next.url, '_blank', 'noopener');
        fetchAllData();
      } else {
        toast.success(message || 'Plan is live — tasks queued');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to execute');
    }
    setExecutingId(null);
  };

  const handleSimulate = async (opportunity) => {
    setSimulateModal({ open: true, opportunity, data: null, loading: true });
    
    try {
      const res = await axios.post(
        `${API}/wealth/simulate-move/${opportunity.opportunity_id}`,
        {},
        { headers: getAuthHeaders() }
      );
      setSimulateModal(prev => ({ ...prev, data: res.data, loading: false }));
    } catch (error) {
      console.error('Simulation failed:', error);
      toast.error('Failed to simulate opportunity');
      setSimulateModal({ open: false, opportunity: null, data: null, loading: false });
    }
  };

  const closeSimulateModal = () => {
    setSimulateModal({ open: false, opportunity: null, data: null, loading: false });
  };

  const handleCreateAction = async (opportunityId) => {
    try {
      await axios.post(
        `${API}/actions/create-from-opportunity/${opportunityId}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('✅ Action packet created! Review in Actions page');
    } catch (error) {
      console.error('Failed to create action:', error);
      toast.error('Failed to create action packet');
    }
  };

  const getRiskColor = (risk) => {
    if (risk <= 3) return 'text-green-500';
    if (risk <= 6) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      income: '💰',
      flips: '🔄',
      business: '🏢',
      investing: '📈',
      credit: '💳',
      invention: '💡',
      real_estate: '🏠',
      services: '🛠️',
      arbitrage: '⚖️'
    };
    return icons[category] || '🎯';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-12 h-12 animate-spin text-[#F59E0B]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header — LEE leads */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Target className="text-[#F59E0B]" />
              Opportunities
            </h1>
            <p className="text-gray-400 mt-1" data-testid="opps-lee-line">
              {opportunities.length > 0
                ? `LEE found ${opportunities.length} live way${opportunities.length !== 1 ? 's' : ''} to make money — the best one is below. You approve, LEE does the work.`
                : 'LEE scans for real, obtainable moves — you approve, LEE does the work'}
            </p>
          </div>
          <button
            onClick={handleScan}
            disabled={scanning}
            className="px-5 py-3 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black rounded-lg flex items-center gap-2 disabled:opacity-50 font-semibold"
          >
            <RefreshCw className={`w-5 h-5 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scanning...' : 'Scan Now'}
          </button>
        </div>

        {/* THIS WEEK'S BEST MOVE — one winner, full math, approve to start */}
        {bestMove?.verdict === 'move' && bestMove.best && (
          <div className="magic-in bg-gradient-to-br from-[#F59E0B]/[0.08] to-transparent bg-[#1C1C1E] border border-[#F59E0B]/40 rounded-lg p-6" data-testid="best-move-hero">
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <span className="px-2 py-0.5 rounded-full bg-[#F59E0B] text-black text-[11px] font-bold uppercase tracking-wider">This week's best move</span>
              <span className="text-[11px] text-gray-500 font-mono">score {bestMove.best.move_score}</span>
            </div>
            <h2 className="text-2xl font-bold text-white mt-2" data-testid="best-move-title">{bestMove.best.title}</h2>
            <p className="text-gray-400 text-sm mt-1">{bestMove.best.summary}</p>
            <div className="mt-4 space-y-1.5" data-testid="best-move-math">
              {(bestMove.best.math || []).map((line, i) => (
                <div key={i} className={`text-sm font-mono ${line.startsWith('Downside') ? 'text-red-300' : 'text-gray-300'}`}>
                  {line.startsWith('Downside') ? '▼ ' : '· '}{line}
                </div>
              ))}
            </div>
            {bestMove.best.why_it_beats_the_rest && (
              <div className="mt-3 text-[12px] text-gray-500 italic">{bestMove.best.why_it_beats_the_rest}</div>
            )}
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <button
                onClick={() => handleStart(bestMove.best.opportunity_id)}
                className="px-5 py-2.5 bg-[#F59E0B] text-black font-bold rounded-lg hover:bg-[#F59E0B]/90 transition-colors"
                data-testid="best-move-approve-btn"
              >
                Approve & Start — LEE queues the first 3 actions
              </button>
              {(bestMove.runners_up || []).map((r) => (
                <span key={r.opportunity_id} className="text-[12px] text-gray-500">
                  runner-up: {r.title} ({r.move_score})
                </span>
              ))}
            </div>
            <div className="mt-3 text-[11px] text-gray-600">{bestMove.honesty}</div>
          </div>
        )}
        {bestMove?.verdict === 'do_nothing' && (
          <div className="bg-[#1C1C1E] border border-gray-700 rounded-lg p-6" data-testid="best-move-do-nothing">
            <span className="px-2 py-0.5 rounded-full bg-gray-700 text-gray-200 text-[11px] font-bold uppercase tracking-wider">Honest call</span>
            <h2 className="text-xl font-bold text-white mt-2">{bestMove.headline}</h2>
            <p className="text-gray-400 text-sm mt-1">{bestMove.reason}</p>
            {(bestMove.rejected || []).map((r, i) => (
              <div key={i} className="mt-2 text-[12px] text-gray-500">✕ {r.title}: {(r.why_rejected || []).join('; ')}</div>
            ))}
          </div>
        )}

        {/* DEAL RADAR — real listings LEE pulled from behind the bot walls */}
        {dealRadar.length > 0 && (
          <div className="magic-in bg-[#1C1C1E] border border-blue-500/25 rounded-lg p-5" style={{ animationDelay: '120ms' }} data-testid="deal-radar">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="text-base font-bold text-white flex items-center gap-2.5">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-60"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-400"></span>
                </span>
                Deal Radar — real listings LEE scanned for you
              </h2>
              <span className="text-[11px] text-gray-500">Zillow · LoopNet · land & business markets, automatic</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {dealRadar.map((d, i) => (
                <a key={i} href={d.url} target="_blank" rel="noreferrer"
                  className="rounded-lg border border-white/10 bg-black/30 p-3.5 hover:border-blue-400/50 transition-colors group" data-testid={`radar-listing-${i}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300">{d.source}</span>
                    {d.opportunity_score != null && <span className="text-[10px] text-gray-500 font-mono">score {d.opportunity_score}</span>}
                  </div>
                  <p className="text-[13px] text-white leading-snug">{(d.title || '').split('|')[0]}</p>
                  <p className="text-sm font-mono text-blue-300 mt-1.5">
                    {d.price ? `$${Number(d.price).toLocaleString()}` : 'price on listing'} <span className="text-gray-600 group-hover:text-blue-400 transition-colors">↗</span>
                  </p>
                </a>
              ))}
            </div>
            <p className="text-[11px] text-gray-600 mt-3">Found by LEE's scanner — not vetted deals. Tap to read the live listing before acting.</p>
          </div>
        )}

        {/* Wealth State Card */}
        <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-xl font-semibold text-white">Your Wealth State</h2>
            {wealthState?.data_source === 'live_plaid_guardian' && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-[11px] font-bold uppercase tracking-wider" data-testid="wealth-state-live-badge">
                LIVE — real accounts
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <div className="text-gray-400 text-sm mb-1">Liquid Cash</div>
              <div className="text-2xl font-bold text-white" data-testid="wealth-state-cash">
                ${(wealthState?.liquid_cash_available || 0).toLocaleString()}
              </div>
            </div>
            <div title={wealthState?.risk_capital_math || ''}>
              <div className="text-gray-400 text-sm mb-1">Risk Capital</div>
              <div className="text-2xl font-bold text-[#F59E0B]" data-testid="wealth-state-risk-capital">
                {wealthState?.risk_capital != null ? `$${Number(wealthState.risk_capital).toLocaleString()}` : '—'}
              </div>
              <div className="text-[10px] text-gray-500">safe to deploy above reserve</div>
            </div>
            <div>
              <div className="text-gray-400 text-sm mb-1">Monthly Cashflow</div>
              <div className={`text-2xl font-bold ${(wealthState?.monthly_free_cashflow || 0) < 0 ? 'text-red-400' : 'text-white'}`} data-testid="wealth-state-cashflow">
                {(wealthState?.monthly_free_cashflow || 0) < 0 ? '−' : ''}${Math.abs(wealthState?.monthly_free_cashflow || 0).toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-gray-400 text-sm mb-1">Risk Tolerance</div>
              <div className="text-lg font-medium text-[#F59E0B] capitalize">
                {wealthState?.risk_tolerance || 'Moderate'}
              </div>
            </div>
            <div>
              <div className="text-gray-400 text-sm mb-1">Time Available</div>
              <div className="text-lg font-medium text-white">
                {wealthState?.time_available_per_week || 10}h/week
              </div>
            </div>
          </div>
          {stats?.last_scan && (
            <div className="mt-4 pt-4 border-t border-gray-700 text-sm text-gray-400">
              Last scan: {new Date(stats.last_scan).toLocaleString()} • Next: {stats.next_scan}
            </div>
          )}
        </div>

        {/* ACTIVE INITIATIVES — income proof tracker */}
        {initiatives.filter((i) => i.status === 'active').length > 0 && (
          <div className="space-y-3" data-testid="initiatives-section">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Play className="w-4 h-4 text-emerald-400" /> Active initiatives — proof tracker
            </h2>
            {initiatives.filter((i) => i.status === 'active').map((ini) => (
              <InitiativeCard key={ini.id} ini={ini} onRefresh={fetchAllData} />
            ))}
          </div>
        )}

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">Total Opportunities</div>
              <div className="text-3xl font-bold text-white">{stats.total_opportunities}</div>
            </div>
            <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">Avg Expected Value</div>
              <div className="text-3xl font-bold text-[#F59E0B]">
                ${(stats.avg_expected_value || 0).toLocaleString()}
              </div>
            </div>
            <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">In Progress</div>
              <div className="text-3xl font-bold text-green-500">{stats.by_status?.in_progress || 0}</div>
            </div>
            <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">Conversion Rate</div>
              <div className="text-3xl font-bold text-blue-500">{stats.conversion_rate || 0}%</div>
            </div>
          </div>
        )}

        {/* Opportunities Feed */}
        <div className="space-y-4">
          {opportunities.length > 3 && (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-lg font-semibold text-white">More ways to make money</h2>
              <button onClick={() => setShowAll(!showAll)} data-testid="see-all-opps-btn"
                className="text-sm text-[#F59E0B] hover:text-[#F59E0B]/80 font-semibold">
                {showAll ? 'Show fewer' : `See all ${opportunities.length} opportunities`}
              </button>
            </div>
          )}
          {opportunities.length > 0 ? (
            (showAll ? opportunities : opportunities.slice(0, 3)).map((opp, idx) => (
              <div key={idx} className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-6 hover:border-[#F59E0B]/50 transition-colors">
                {/* Header */}
                <div className="flex items-start justify-between mb-4 gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                      <span className="text-2xl">{getCategoryIcon(opp.category)}</span>
                      <h3 className="text-xl font-semibold text-white">{opp.title}</h3>
                      <span className="px-2 py-1 bg-[#F59E0B]/20 text-[#F59E0B] rounded text-xs font-medium capitalize">
                        {opp.category}
                      </span>
                      {opp.burn_coverage_pct != null && opp.burn_coverage_pct > 0 && (
                        <span className="px-2 py-1 bg-emerald-500/15 text-emerald-400 rounded text-xs font-medium" data-testid="burn-coverage-chip">
                          covers ~{opp.burn_coverage_pct}% of monthly burn
                        </span>
                      )}
                      {String(opp.source || '').startsWith('live_') && (
                        <span className="px-2 py-1 bg-blue-500/15 text-blue-400 rounded text-xs font-bold uppercase" data-testid="live-signal-chip">
                          Live signal
                        </span>
                      )}
                      {opp.evidence_url && (
                        <a href={opp.evidence_url} target="_blank" rel="noreferrer"
                          className="px-2 py-1 bg-white/[0.06] text-gray-300 rounded text-xs hover:bg-white/10 transition-colors" data-testid="evidence-link">
                          View live source ↗
                        </a>
                      )}
                      {opp.verified_live_at && (
                        <span className="px-2 py-1 bg-emerald-500/10 text-emerald-300 rounded text-xs" data-testid="verified-live-chip">
                          ✓ verified live {new Date(opp.verified_live_at).toLocaleDateString() === new Date().toLocaleDateString() ? 'today' : new Date(opp.verified_live_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-400">{opp.summary}</p>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-2xl font-bold text-[#F59E0B] mb-1">
                      {opp.score?.toFixed(0)}
                    </div>
                    <div className="text-xs text-gray-400">fit score</div>
                  </div>
                </div>

                {/* Why it fits */}
                <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg p-3 mb-4">
                  <p className="text-sm text-[#F59E0B]">
                    <strong>Why this fits:</strong> {opp.why_it_fits_owner}
                  </p>
                </div>

                {/* Effort + Guardian check */}
                {(opp.your_effort || opp.guardian_check?.note) && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {opp.your_effort && (
                      <span className="px-2.5 py-1.5 bg-white/[0.04] border border-white/10 rounded-lg text-xs text-gray-300" data-testid="effort-chip">
                        <strong className="text-white">Your effort:</strong> {opp.your_effort}
                      </span>
                    )}
                    {opp.guardian_check?.note && (
                      <span className="px-2.5 py-1.5 bg-emerald-500/[0.06] border border-emerald-500/25 rounded-lg text-xs text-emerald-300 inline-flex items-center gap-1.5" data-testid="guardian-chip">
                        <Shield className="w-3 h-3 shrink-0" /> {opp.guardian_check.note}
                      </span>
                    )}
                  </div>
                )}

                {/* Metrics */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Min Capital</div>
                    <div className="text-white font-semibold">${opp.min_capital?.toLocaleString() || '0'}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Expected Value</div>
                    <div className="text-green-400 font-semibold">
                      ${(opp.expected_value?.base ?? opp.expected_value_range?.base)?.toLocaleString() || '0'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Risk Rating</div>
                    <div className={`font-semibold ${getRiskColor(opp.risk_rating)}`}>
                      {opp.risk_rating}/10
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Time to Start</div>
                    <div className="text-white font-medium text-sm">{opp.estimated_time_to_start}</div>
                  </div>
                </div>

                {/* How you actually get it — LEE vs YOU */}
                {(opp.plan?.length > 0 || opp.steps_to_execute?.length > 0) && (
                  <div className="border-t border-gray-700 pt-4 mb-4">
                    <div className="text-sm font-medium text-white mb-2">How you actually get it:</div>
                    {opp.plan?.length > 0 ? (
                      <ol className="space-y-1.5" data-testid="plan-steps">
                        {opp.plan.map((s, i) => (
                          <li key={i} className="text-sm flex items-start gap-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 mt-0.5 ${s.who === 'lee' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'bg-blue-500/20 text-blue-300'}`}>
                              {s.who === 'lee' ? 'LEE' : 'YOU'}
                            </span>
                            <span className={s.who === 'lee' ? 'text-gray-500' : 'text-gray-300'}>{s.step}</span>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <ol className="space-y-1">
                        {opp.steps_to_execute.slice(0, 3).map((step, i) => (
                          <li key={i} className="text-gray-400 text-sm flex items-start gap-2">
                            <span className="text-[#F59E0B] font-bold">{i + 1}.</span>
                            {step}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  {(opp.status === 'new' || opp.status === 'saved') && (
                    <>
                      <button
                        onClick={() => handleExecute(opp.opportunity_id)}
                        disabled={executingId === opp.opportunity_id}
                        className="flex-1 min-w-[170px] px-4 py-2.5 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black rounded-lg flex items-center justify-center gap-2 font-bold disabled:opacity-60"
                        data-testid="execute-opportunity-btn"
                      >
                        {executingId === opp.opportunity_id
                          ? <RefreshCw className="w-4 h-4 animate-spin" />
                          : <Zap className="w-4 h-4" />}
                        {executingId === opp.opportunity_id ? 'LEE is starting it…' : 'Execute — LEE starts it'}
                      </button>
                      <button
                        onClick={() => handleSimulate(opp)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center gap-2"
                        data-testid="simulate-btn"
                      >
                        <BarChart3 className="w-4 h-4" />
                        Simulate
                      </button>
                      {opp.status === 'new' && (
                        <>
                          <button
                            onClick={() => handleSave(opp.opportunity_id)}
                            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                            data-testid="save-opportunity-btn"
                          >
                            <Bookmark className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleIgnore(opp.opportunity_id)}
                            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                            data-testid="ignore-opportunity-btn"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </>
                  )}
                  {opp.status === 'in_progress' && (
                    <>
                      <div className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg flex items-center justify-center gap-2 font-medium" data-testid="in-motion-badge">
                        <Play className="w-4 h-4" />
                        In motion
                      </div>
                      {opp.project_id ? (
                        <button
                          onClick={() => navigate(`/inventions/${opp.project_id}/builders`)}
                          className="flex-1 min-w-[150px] px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black rounded-lg flex items-center justify-center gap-2 font-bold"
                          data-testid="open-build-btn"
                        >
                          <Hammer className="w-4 h-4" />
                          Open the build
                        </button>
                      ) : (
                        <button
                          onClick={() => document.querySelector('[data-testid="initiative-card"]')?.scrollIntoView({ behavior: 'smooth' })}
                          className="flex-1 min-w-[150px] px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center justify-center gap-2"
                          data-testid="view-plan-btn"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View plan & tasks
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-12 text-center">
              <Target className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">No opportunities found yet</p>
              <button
                onClick={handleScan}
                className="px-6 py-3 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black rounded-lg font-semibold"
              >
                Run First Scan
              </button>
            </div>
          )}
        </div>

        {/* Info Notice */}
        <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-4">
          <div className="flex items-start gap-3 text-sm">
            <Shield className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <div className="text-gray-400">
              <strong className="text-white">Intelligence-Driven:</strong> Opportunities are personalized to your capital, risk tolerance, and time. 
              Starting an opportunity creates a tracked initiative. All actions logged.
            </div>
          </div>
        </div>
      </div>

      {/* Simulate Modal */}
      {simulateModal.open && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={closeSimulateModal}>
          <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="simulate-modal">
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#1C1C1E] border-b border-gray-800 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <BarChart3 className="text-blue-400" />
                  Wealth Move Simulation
                </h2>
                <p className="text-gray-400 mt-1">{simulateModal.opportunity?.title}</p>
              </div>
              <button
                onClick={closeSimulateModal}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                data-testid="close-simulate-modal"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {simulateModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-12 h-12 animate-spin text-blue-400" />
                </div>
              ) : simulateModal.data ? (
                <div className="space-y-6">
                  {/* Current State */}
                  <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-5">
                    <h3 className="text-lg font-semibold text-white mb-4">Current State</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-gray-400 text-sm mb-1">Net Worth</div>
                        <div className="text-2xl font-bold text-white" data-testid="current-net-worth">
                          ${(simulateModal.data.current_state?.net_worth || 0).toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-sm mb-1">Liquid Assets</div>
                        <div className="text-2xl font-bold text-white" data-testid="current-liquid">
                          ${(simulateModal.data.current_state?.liquid_assets || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Projections */}
                  {['3_months', '6_months', '12_months'].map((period) => {
                    const projection = simulateModal.data[period];
                    if (!projection) return null;

                    return (
                      <div key={period} className="bg-[#0A0A0A] border border-blue-500/30 rounded-lg p-5">
                        <h3 className="text-lg font-semibold text-blue-400 mb-4">
                          {period.replace('_', ' ').replace('months', 'Months')} Projection
                        </h3>

                        {/* Conservative / Base / Upside */}
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="text-center p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <div className="text-red-400 text-xs font-medium mb-2">Conservative</div>
                            <div className="text-xl font-bold text-white" data-testid={`${period}-conservative-nw`}>
                              ${(projection.conservative?.net_worth || 0).toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {projection.conservative?.net_worth_delta > 0 ? '+' : ''}
                              ${(projection.conservative?.net_worth_delta || 0).toLocaleString()}
                            </div>
                          </div>

                          <div className="text-center p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                            <div className="text-yellow-400 text-xs font-medium mb-2">Base Case</div>
                            <div className="text-xl font-bold text-white" data-testid={`${period}-base-nw`}>
                              ${(projection.base?.net_worth || 0).toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {projection.base?.net_worth_delta > 0 ? '+' : ''}
                              ${(projection.base?.net_worth_delta || 0).toLocaleString()}
                            </div>
                          </div>

                          <div className="text-center p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                            <div className="text-green-400 text-xs font-medium mb-2">Upside</div>
                            <div className="text-xl font-bold text-white" data-testid={`${period}-upside-nw`}>
                              ${(projection.upside?.net_worth || 0).toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {projection.upside?.net_worth_delta > 0 ? '+' : ''}
                              ${(projection.upside?.net_worth_delta || 0).toLocaleString()}
                            </div>
                          </div>
                        </div>

                        {/* Liquidity Impact */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="text-center p-2 bg-black/30 rounded">
                            <div className="text-xs text-gray-400 mb-1">Liquid Change</div>
                            <div className="text-sm font-medium text-red-400">
                              ${(projection.conservative?.liquid_delta || 0).toLocaleString()}
                            </div>
                          </div>
                          <div className="text-center p-2 bg-black/30 rounded">
                            <div className="text-xs text-gray-400 mb-1">Liquid Change</div>
                            <div className="text-sm font-medium text-yellow-400">
                              ${(projection.base?.liquid_delta || 0).toLocaleString()}
                            </div>
                          </div>
                          <div className="text-center p-2 bg-black/30 rounded">
                            <div className="text-xs text-gray-400 mb-1">Liquid Change</div>
                            <div className="text-sm font-medium text-green-400">
                              ${(projection.upside?.liquid_delta || 0).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Capital Required */}
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-sm">
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      <div>
                        <span className="text-yellow-400 font-medium">Capital Required:</span>
                        <span className="text-white ml-2" data-testid="capital-required">
                          ${(simulateModal.data.capital_required || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400">No simulation data available</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-[#1C1C1E] border-t border-gray-800 p-6">
              <button
                onClick={closeSimulateModal}
                className="w-full px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <Toaster position="top-right" />
    </Layout>
  );
};

export default Opportunities;
